import { ASPECT_RATIOS, DEFAULT_IMAGE_GALLERY_FILTERS } from 'Pods/LazyLoaded/ImageGalleryPanel/constants'

import aircraft from 'images/three_d_studio/aircraft.jpg'
import animals from 'images/three_d_studio/animals.jpg'
import car from 'images/three_d_studio/car.jpg'
import character from 'images/three_d_studio/character.jpg'
import generated1 from 'images/three_d_studio/generated-1.jpg'

// Stand-in for the community image endpoint until the backend ships. Everything
// here is deterministic so the masonry layout is stable across reloads.

const CATEGORY_VALUES = [
  'aircraft',
  'animals',
  'architectural',
  'exterior',
  'interior',
  'car',
  'character',
  'military',
  'furniture',
  'food',
  'plant',
  'science',
  'space',
  'sports',
  'vehicle',
  'watercraft',
]

// Labels are translation keys: the host resolves them with the page locale so
// the chips match the rest of the panel on locale-prefixed URLs.
export const MOCK_IMAGE_CATEGORIES = [DEFAULT_IMAGE_GALLERY_FILTERS.category, ...CATEGORY_VALUES]
  .map(value => ({ nameKey: `imageGallery.categories.${value}`, value }))

// Bundled studio assets rather than a third-party placeholder host, so the mock
// gallery never sends visitors' requests off-site. The card crops with
// `object-fit: cover`, so any photo fits every aspect ratio.
const IMAGE_SOURCES = [aircraft, animals, car, character, generated1]

const SUBJECTS = [
  'Abstract stone sculpture',
  'Coloured pencils in ceramic holders',
  'Electric aircraft on a runway',
  'Desert skyline at golden hour',
  'Fennec fox in the sand',
  'Stylised girl portrait',
  'Porcelain bunnies in eggshells',
  'Astronaut in an orange suit',
  'Red finch mid-flight',
  'Humanoid robot on concrete',
  'Rose gold diamond ring',
  'Clock tower against grey sky',
  'Toy propeller plane',
  'Rainbow butterfly swarm',
  'Tropical fish with flowing fins',
  'Vintage roadster on wet asphalt',
  'Mid-century lounge chair',
  'Neon night market alley',
  'Snowy mountain cabin',
  'Chrome espresso machine',
  'Origami crane in paper forest',
  'Sci-fi corridor with blue light',
  'Cozy reading nook',
  'Wooden sailing ship model',
]

const PROMPTS = [
  'Young girl character, around 10 years old, with bright, curly auburn hair that bounces as she '
    + 'plays. She wears a colorful dress adorned with whimsical patterns of flowers and butterflies, '
    + 'reflecting her playful spirit. Her big, expressive green eyes sparkle with curiosity and '
    + 'mischief as she explores a sunlit meadow filled with wildflowers.',
  'Studio product shot of a matte ceramic object on a seamless grey backdrop, soft top light, '
    + 'shallow depth of field, ultra detailed, photorealistic.',
  'Isometric low-poly diorama with pastel colours, clean edges and a single warm light source, '
    + 'rendered as a collectible toy on a white table.',
  'Cinematic wide shot at golden hour, volumetric haze, high dynamic range, crisp textures and a '
    + 'calm, quiet mood.',
]

const CREATORS = [
  'creative-master', 'pixel-forge', 'render-nomad', 'studio-luma', 'voxel-vera', 'polyglot-3d',
]

const BASE_MODELS = ['Flux Dev', 'Flux Pro', 'SDXL']
const STYLES = ['None', 'Cinematic', '3D Render', 'Anime']

// Output size for each generator ratio at the 1MP tier.
const RESOLUTIONS = {
  '1:1': '1024 x 1024',
  '2:3': '832 x 1248',
  '3:2': '1248 x 832',
  '9:16': '768 x 1344',
  '16:9': '1344 x 768',
}

const RATIO_KEYS = Object.keys(ASPECT_RATIOS)
const MOCK_IMAGE_COUNT = 120
const FIRST_CREATED_AT = Date.UTC(2026, 6, 21)
const DAY_MS = 24 * 60 * 60 * 1000
export const MOCK_RESPONSE_DELAY_MS = 350
export const SIMILAR_IMAGES_COUNT = 6

const buildImage = (index) => {
  const id = index + 1
  const ratioKey = RATIO_KEYS[(index * 7) % RATIO_KEYS.length]
  const creatorIndex = (index * 5) % CREATORS.length

  return {
    id,
    title: `${SUBJECTS[index % SUBJECTS.length]} ${Math.floor(index / SUBJECTS.length) + 1}`,
    category: CATEGORY_VALUES[index % CATEGORY_VALUES.length],
    aspectRatio: ASPECT_RATIOS[ratioKey],
    src: IMAGE_SOURCES[index % IMAGE_SOURCES.length],
    prompt: PROMPTS[index % PROMPTS.length],
    createdAt: new Date(FIRST_CREATED_AT - index * DAY_MS).toISOString(),
    user: {
      username: CREATORS[creatorIndex],
      avatar_url: IMAGE_SOURCES[creatorIndex % IMAGE_SOURCES.length],
    },
    settings: {
      // Every third image was generated from a reference; the rest are text only.
      reference: index % 3 === 0 ? IMAGE_SOURCES[(index + 1) % IMAGE_SOURCES.length] : null,
      baseModel: BASE_MODELS[index % BASE_MODELS.length],
      aspectRatio: ratioKey,
      style: STYLES[(index * 3) % STYLES.length],
      resolution: RESOLUTIONS[ratioKey],
      seed: 3161985 + index * 7919,
    },
  }
}

export const MOCK_IMAGES = Array.from({ length: MOCK_IMAGE_COUNT }, (_, index) => buildImage(index))

// Same-category images first, then the rest, never the image itself.
export const getSimilarImages = (image, count = SIMILAR_IMAGES_COUNT) => {
  const others = MOCK_IMAGES.filter(candidate => candidate.id !== image.id)
  const sameCategory = others.filter(candidate => candidate.category === image.category)
  const rest = others.filter(candidate => candidate.category !== image.category)

  return [...sameCategory, ...rest].slice(0, count)
}

const matchesFilters = (image, { searchQuery, category }) => {
  const matchesCategory = category === DEFAULT_IMAGE_GALLERY_FILTERS.category || image.category === category
  const query = searchQuery.trim().toLowerCase()
  const matchesQuery = !query || image.title.toLowerCase().includes(query)

  return matchesCategory && matchesQuery
}

// Mirrors the { data, meta } shape of the community gallery endpoint so swapping
// in the real request later only touches the fetcher.
export const fetchMockImages = ({ page, perPage, searchQuery, category }) => new Promise((resolve) => {
  const filtered = MOCK_IMAGES.filter(image => matchesFilters(image, { searchQuery, category }))
  const start = (page - 1) * perPage

  setTimeout(() => {
    resolve({
      data: filtered.slice(start, start + perPage),
      meta: {
        currentPage: page,
        perPage,
        totalCount: filtered.length,
        totalPages: Math.ceil(filtered.length / perPage),
      },
    })
  }, MOCK_RESPONSE_DELAY_MS)
})
